import React, {useMemo, useState} from "react";
import {Col, Form, Modal, Row, Select} from "antd";
import {useFormik} from "formik";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {bloodTypes, managerCompanyOptions} from "constants/constants";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import CityInput from "../cityInput/CityInput";
import DistrictInput from "../districtInput/DistrictInput";
import {FormDatePicker, FormInput, FormSelect, MaskedInputField} from "../formFields/FormFields";
import {createCourierCandidateValidation} from "./createCourierCandidateValidation";

const initialValues = {
    fullname: "",
    tckn: "",
    cellPhone: "",
    email: "",
    gender: undefined,
    bloodGroup: undefined,
    birthDate: undefined,
    motorTypeId: undefined,
    drivingLicenseIds: [],
    preferredCityId: undefined,
    preferredDistrictIds: [],
    managerCompany: undefined
};

export const CreateCourierCandidateModal = ({
    visibility,
    onCancel = () => {},
    callbackFunc = () => {}
}) => {
    const {t} = useTranslation(["common"]);
    const stores = useStore();
    const [loading, setLoading] = useState(false);

    const [motorTypesLoading, motorTypes] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.listMotorTypes.bind(null, {}),
        condition: visibility,
        dependencies: [visibility]
    });

    const [drivingLicenseTypesLoading, drivingLicenseTypes] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.listDrivingLicenseTypes.bind(null, {}),
        condition: visibility,
        dependencies: [visibility]
    });

    //FormSelect iç içe alanlara erişemediği için seçenekler önceden düzleştirilir.
    const motorTypeOptions = useMemo(
        () =>
            (Array.isArray(motorTypes) ? motorTypes : []).map(motorType => ({
                _id: motorType?._id,
                name: motorType?.label?.tr || motorType?.label?.en || "-"
            })),
        [motorTypes]
    );

    const formik = useFormik({
        initialValues,
        validationSchema: createCourierCandidateValidation,
        onSubmit: async values => {
            setLoading(true);
            try {
                const result = await stores.courierStore.createCourierCandidate({
                    fullname: values.fullname.trim().replace(/\s+/g, " "),
                    tckn: values.tckn.trim(),
                    cellPhone: values.cellPhone.replace(/[-()\s]/g, ""),
                    email: values.email.trim().toLowerCase(),
                    gender: values.gender,
                    bloodGroup: values.bloodGroup,
                    birthDate: moment(values.birthDate).format("YYYY-MM-DD"),
                    motorTypeId: values.motorTypeId,
                    drivingLicenseIds: values.drivingLicenseIds,
                    preferredCityId: values.preferredCityId,
                    preferredDistrictIds: values.preferredDistrictIds,
                    managerCompany: values.managerCompany
                });
                if (result) {
                    callbackFunc();
                    formik.resetForm();
                    onCancel();
                }
            } finally {
                setLoading(false);
            }
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        setFieldTouched,
        handleSubmit
    } = formik;

    const handleCancel = () => {
        formik.resetForm();
        onCancel();
    };

    const fieldStatus = fieldKey => ({
        validateStatus: errors[fieldKey] && touched[fieldKey] ? "error" : "success",
        help: errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null
    });

    if (!visibility) return null;

    return (
        <Modal
            title="Yeni Kurye Oluştur"
            visible={visibility}
            width={720}
            destroyOnClose
            onCancel={handleCancel}
            footer={[
                <CustomButton key="cancel" data-testid="cancelButton" onClick={handleCancel}>
                    İptal
                </CustomButton>,
                <CustomButton
                    key="submit"
                    type="primary"
                    data-testid="submitButton"
                    loading={loading}
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    Oluştur
                </CustomButton>
            ]}
        >
            <Form layout="vertical">
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                        <FormInput
                            label="İsim Soyisim"
                            fieldKey="fullname"
                            required
                            value={values.fullname}
                            error={errors.fullname}
                            touched={touched.fullname}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormInput
                            label="TCKN"
                            fieldKey="tckn"
                            required
                            value={values.tckn}
                            error={errors.tckn}
                            touched={touched.tckn}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <MaskedInputField
                            label="Telefon Numarası"
                            fieldKey="cellPhone"
                            required
                            value={values.cellPhone}
                            error={errors.cellPhone}
                            touched={touched.cellPhone}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormInput
                            label="Mail"
                            fieldKey="email"
                            required
                            value={values.email}
                            error={errors.email}
                            touched={touched.email}
                            onChange={handleChange}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            label="Cinsiyet"
                            fieldKey="gender"
                            required
                            t={t}
                            textFromi18n
                            data={["male", "female"]}
                            value={values.gender}
                            error={errors.gender}
                            touched={touched.gender}
                            onChange={value => setFieldValue("gender", value)}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            label="Kan Grubu"
                            fieldKey="bloodGroup"
                            required
                            data={bloodTypes}
                            value={values.bloodGroup}
                            error={errors.bloodGroup}
                            touched={touched.bloodGroup}
                            onChange={value => setFieldValue("bloodGroup", value)}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormDatePicker
                            label="Doğum Tarihi"
                            fieldKey="birthDate"
                            required
                            value={values.birthDate}
                            error={errors.birthDate}
                            touched={touched.birthDate}
                            onChange={value => setFieldValue("birthDate", value)}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            label="Motor Tipi"
                            fieldKey="motorTypeId"
                            required
                            mapKey="_id"
                            mapValue="name"
                            data={motorTypeOptions}
                            disabled={motorTypesLoading}
                            value={values.motorTypeId}
                            error={errors.motorTypeId}
                            touched={touched.motorTypeId}
                            onChange={value => setFieldValue("motorTypeId", value)}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Ehliyet" required {...fieldStatus("drivingLicenseIds")}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{width: "100%"}}
                                data-testid="drivingLicenseIdsSelect"
                                loading={drivingLicenseTypesLoading}
                                value={values.drivingLicenseIds}
                                onChange={value => setFieldValue("drivingLicenseIds", value)}
                            >
                                {drivingLicenseTypes?.result?.map(drivingLicenseType => (
                                    <Select.Option
                                        key={drivingLicenseType._id}
                                        value={drivingLicenseType._id}
                                        data-testid="drivingLicenseIdsSelect_option"
                                    >
                                        {drivingLicenseType?.label?.tr}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Çalışmak İstenilen İl"
                            required
                            {...fieldStatus("preferredCityId")}
                        >
                            <CityInput
                                optionMappingKey="_id"
                                value={values.preferredCityId}
                                onChange={value => {
                                    setFieldValue("preferredCityId", value);
                                    setFieldValue("preferredDistrictIds", []);
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Çalışmak İstenilen İlçe"
                            required
                            {...fieldStatus("preferredDistrictIds")}
                        >
                            <DistrictInput
                                optionMappingKey="_id"
                                selectedCityId={values.preferredCityId}
                                value={values.preferredDistrictIds}
                                onChange={value => setFieldValue("preferredDistrictIds", value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Yönetici Firma"
                            required
                            {...fieldStatus("managerCompany")}
                        >
                            <Select
                                data-testid="managerCompanySelect"
                                value={values.managerCompany}
                                onChange={value => setFieldValue("managerCompany", value)}
                                onBlur={() => setFieldTouched("managerCompany", true)}
                                placeholder="Seçiniz"
                            >
                                {managerCompanyOptions.map(option => (
                                    <Select.Option
                                        key={option.value}
                                        value={option.value}
                                        data-testid="managerCompanySelect_option"
                                    >
                                        {option.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
